import type { ClientItemId } from "owa-client-ids";
import type { MailboxInfo } from "owa-client-types";
import { owaComputedFn } from "owa-computed-fn";
import { getCmdIdFromRibbonId } from "owa-mail-ribbon-store/lib/util/mailRibbonIdToCmdIdMap";
import { getIsPopoutButtonDisabled } from "./getIsPopoutButtonDisabled";
import { getMailMenuItemShouldShow, getMailMenuItemShouldDisable, isScheduleWithCopilotSupportedForSelectedItem, getItemOrMessage, } from "owa-mail-filterable-menu-behavior";
import { getReadRibbonBaseId } from "./getReadRibbonId";
import { type MenuItemType } from "owa-filterable-menu/lib/components/MenuItemType";
import { isNotesFolderSelected } from "./isNotesFolderSelected";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { getSelectedTableView, isMultipleSelection, isSelectionWithinSingleMailbox, isItemPartOperation, isMultipleSelectedItemParts, MailRowDataPropertyGetter, } from "owa-mail-list-store";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import { isReadingPaneStackedView } from "owa-mail-organization-settings/lib/utils/readingPaneStackedViewUtils";
import { getComposeViewState } from "owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState";
import getLastestNonDraftItemId from "owa-mail-reading-pane-store-conversation/lib/utils/getLastestNonDraftItemId";
import readingPaneStore from "owa-mail-reading-pane-store/lib/store/Store";
import { getTabIdFromTargetWindow } from "owa-mail-reading-pane-store/lib/utils/getTabIdFromTargetWindow";
import mailStore from "owa-mail-store/lib/store/Store";
import getItemRightsManagementRestrictions from "owa-mail-store/lib/utils/getItemRightsManagementRestrictions";
import { type MailRibbonControlId } from "owa-ribbon-ids/lib/mailRibbonId";
import type ReactListViewType from "owa-service/lib/contract/ReactListViewType";
import { getActiveContentTab, TabType } from "owa-tab-store";

export const getIsReadButtonDisabled: (controlId: number | string, isPopout: boolean, mailboxInfo: MailboxInfo, itemId: ClientItemId | undefined) => boolean = owaComputedFn((controlId: number | string, isPopout: boolean, mailboxInfo: MailboxInfo, itemId: ClientItemId | undefined): boolean => {
    // if we're in a read only pop out, use getIsPopoutButtonDisabled instead
    if (isPopout) {
        return getIsPopoutButtonDisabled(getReadActionControlId(controlId), itemId);
    }
    else {
        // Notes folders in an archive mailbox don't support mail item actions.
        if (isNotesFolderSelected()) {
            return true;
        }
        
        const id: number = getNumericRibbonId(controlId);
        if ((id === 696 ||
            id === 699) &&
            !isScheduleWithCopilotSupportedForSelectedItem()) {
            return true;
        }
        
        const cmdId = getCmdIdFromRibbonId(id, mailboxInfo);
        switch (cmdId) {
            case 83:
            case 86:
            case 92:
                return getMailMenuItemShouldDisable(cmdId);
            case 52:
                return getIsForwardButtonDisabled(mailboxInfo);
            case 40:
                return getIsMainWindowPrintDisabled(mailboxInfo);
            default:
                return !getMailMenuItemShouldShow(cmdId, mailboxInfo);
        }
    }
});

const getReadActionControlId = owaComputedFn(function getReadActionControlId(id: string | number): number {
    if (typeof id === "string") {
        const baseId = getReadRibbonBaseId(id);
        return getNumericRibbonId(baseId);
    }
    else {
        return id;
    }
});

function getNumericRibbonId(id: number | string): number {
    return typeof id == "string" ? parseInt(id) : id;
}

function getIsForwardButtonDisabled(mailboxInfo: MailboxInfo): boolean {
    if (isGlobalFeatureEnabled("doc-forward-multiple-attachments")) {
        const tableView = getSelectedTableView(true /* getEffectiveTableView */);
        if (tableView && isMultipleSelection(tableView)) {
            return !isSelectionWithinSingleMailbox(getMailboxInfo(tableView), tableView);
        }
    }
    
    return !getMailMenuItemShouldShow(52, mailboxInfo);
}

export function getIsPrintInProgress(projectionTabId?: string): boolean {
    const tabId = projectionTabId || getTabIdFromTargetWindow();
    return !!readingPaneStore().itemPrintPaneViewStates.get(tabId)?.isPrint;
}

function getIsMainWindowPrintDisabled(mailboxInfo: MailboxInfo): boolean {
    if (isMultipleSelectedItemParts() ||
        getComposeViewState()?.isComposeRendered ||
        !getMailMenuItemShouldShow(40, mailboxInfo)) {
        return true;
    }
    
    const { item: selectedItem, canLoadConversation } = getPrintTarget();
    const item = selectedItem?.ItemId?.Id
        ? (mailStore().items.get(selectedItem.ItemId.Id) ?? selectedItem)
        : selectedItem;
    // Conversation printing loads missing reading-pane state before validating item rights.
    return item ? !getItemRightsManagementRestrictions(item).PrintAllowed : !canLoadConversation;
}

function getPrintTarget() {
    const activeTab = getActiveContentTab();
    if (activeTab?.type === TabType.SecondaryReadingPane) {
        const { id, listViewType } = activeTab.data;
        const canLoadConversation = listViewType === 0 &&
            isReadingPaneStackedView(undefined) &&
            !!id.Id;
        const itemId = canLoadConversation ? getLastestNonDraftItemId(id.Id) : id.Id;
        return { item: mailStore().items.get(itemId), canLoadConversation };
    }
    
    const tableView = getSelectedTableView();
    if (!tableView) {
        return { item: undefined, canLoadConversation: false };
    }
    if (!isItemPartOperation() &&
        tableView.tableQuery.listViewType === 0) {
        const rowKey = [...tableView.selectedRowKeys.keys()][0];
        if (rowKey) {
            const conversationId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);
            if (conversationId?.Id) {
                const itemId = getLastestNonDraftItemId(conversationId.Id);
                return {
                    item: itemId ? mailStore().items.get(itemId) : getItemOrMessage(tableView),
                    canLoadConversation: true,
                };
            }
        }
    }
    return { item: getItemOrMessage(tableView), canLoadConversation: false };
}
